/**
 Copyright (c) 2021 Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/oj-vb-switcher/switcherViewModel',[
  'ojs/ojcomponentcore',
  'vb/private/stateManagement/router',
  'vb/private/constants',
  'vb/private/log',
], (Components, Router, Constants, Log) => {
  /** @typedef {import('ojs/ojcomposite').PropertyChangedContext} PropertyChangedContext */
  /** @typedef {import('ojs/ojcomposite').ViewModelContext} ViewModelContext */
  /** @typedef {import('ojs/ojarraydataprovider')} ArrayDataProvider */
  const logger = Log.getLogger('/vb/components/oj-vb-switcher');

  function hide(item) {
    const isCurrentlyVisible = !(item.style.display === 'none');
    if (isCurrentlyVisible) {
      item.style.setProperty('display', 'none');
      Components.subtreeHidden(item);
    }
  }

  function show(item) {
    const isCurrentlyVisible = !(item.style.display === 'none');
    if (!isCurrentlyVisible) {
      item.style.removeProperty('display');
    }
    Components.subtreeShown(item);
  }

  /**
   * This is used when the array data provider mutate event has some items to add
   * in the item to remove. In this case we only need to keep the item to remove.
   *
   * @param   {Object} eventDetail - The detail object of the mutate event
   * @return  {{removeArray: Array<Object>, addArray: Array<Object>}} - The array of elements to add and remove
   */
  const resolveArrayMutation = (eventDetail) => {
    const addArray = eventDetail.add.data || [];
    const remove = eventDetail.remove.data || [];
    // Remove the items added from the items to remove
    // and from the items to add
    const removeArray = remove.filter((x) => !addArray.find((y, index) => {
      if (y.id === x.id) {
        addArray.splice(index, 1);
        return true;
      }
      return false;
    }));

    return { removeArray, addArray };
  };

  /**
   * ViewModel class for the oj-vb-switcher CCA
   * @category Component
   */
  class SwitcherViewModel {
    constructor(context) {
      const props = context.properties;
      this.composite = context.element;
      this.properties = context.properties;
      // Used to keep a reference to all ADP listeners in order to clean them later
      this.registeredListeners = {};
      // Promise used to chain changes to the currentItem property
      this._currentItemChangedPromise = Promise.resolve();
      // The switcher element id currently displayed in the DOM.
      this._displayedItem = '';

      if (!props.data) {
        const errMsg = 'A data property was not provided.'
          + ' This is required in order to load and render the content of a switcher.'
          + ' Check the configuration of oj-vb-switcher component!';
        throw new Error(errMsg);
      }

      // Create the switcher bridge
      const bridgeFactory = props.bridge;
      if (!bridgeFactory) {
        const errMsg = 'The \'bridge\' property was not configured on the oj-vb-switcher component. Set '
          + 'the  \'bridge\' property to "[[ vbBridge ]]".';
        throw new Error(errMsg);
      }
      this.bridge = bridgeFactory.create(Constants.ComponentBridgeType.SWITCHER, props);

      /**
       * This is the value for the data property of the <oj-bind-for-each> in the view of the switcher
       * @type {ArrayDataProvider}
       */
      this.moduleADP = this.bridge.getModuleArrayDataProvider();

      this._initADP(props.data);
    }

    /**
     * Attempt to select the switcher element matching the currentItem property if one exists.
     * @private
     */
    _selectCurrentItem() {
      const currentSwitcherElt = this.bridge.getCurrentSwitcherElement();
      let initPromise;
      if (currentSwitcherElt && !currentSwitcherElt.root.isInitialized()) {
        // When switching at the time the switcher is created, call initialize with replace=true
        // so it does not push the history state.
        initPromise = currentSwitcherElt.initialize(true);
      } else {
        initPromise = Promise.resolve();
      }

      Router.setBusyState();
      return initPromise
        .then(() => this._displayCurrentElement(currentSwitcherElt))
        .finally(() => {
          Router.clearBusyState();
        });
    }

    /**
     * Serialize all changes that can replace switcher content or select a switcher item.
     * A data-provider refresh removes initialized elements asynchronously. Letting a currentItem
     * change run while that removal is in progress can apply the navigation for the old item to
     * the new item that is added by the refresh.
     *
     * @param {function(): Promise|undefined} operation
     * @return {Promise}
     * @private
     */
    _queueSwitcherOperation(operation) {
      const operationPromise = this._currentItemChangedPromise.then(operation);
      // Keep processing later data-provider events if an earlier operation fails. Individual
      // currentItem changes still handle their own error by restoring the previous value.
      this._currentItemChangedPromise = operationPromise.catch((error) => {
        logger.error(error);
      });
      return operationPromise;
    }

    /**
     * Initializes the Array Data Provider passed in the data property of the switcher component
     *
     * @param {Object} adp - The ArrayDataProvider
     * @return {Promise} a promise that resolves after the initial data refresh
     * @private
     */
    _initADP(adp) {
      // A typical error is to use an Array instead of an ADP
      // All ADPs have the data property set to an Array
      if (!adp || !Array.isArray(adp.data) || typeof adp.addEventListener !== 'function') {
        throw new Error('The data property of the oj-vb-switcher component should be an ArrayDataProvider.');
      }

      // Keep a reference to the listener callback to remove them later
      this.registeredListeners.mutate = (event) => {
        this._mutateADPListener(event);
      };

      // Listen to changes of the data property of type ADP
      adp.addEventListener('mutate', this.registeredListeners.mutate);

      // Keep a reference to the listener callback to remove them later
      this.registeredListeners.refresh = () => {
        this._refreshADP(this.properties.data);
      };

      // Listen to the refresh event of data property of type ADP
      adp.addEventListener('refresh', this.registeredListeners.refresh);

      // Initialize the switcher with the initial elements in the switcher array
      return this._refreshADP(adp);
    }

    /**
     * Composite lifecycle callback
     * Invoked after the View is first inserted into the DOM and then each time the composite is reconnected to the
     * DOM after being disconnected. Note that if the composite needs to add/remove event listeners, we recommend
     * using this and the disconnected methods.
     * @param {ViewModelContext} context
     */
    connected(context) {
      this.bridge.connected(context);
    }

    /**
     * Composite lifecycle callback called when the composite component is disconnected from the DOM.
     * Invoked when this composite component is disconnected from the DOM.
     * @param {Element} element
     */
    disconnected(element) {
      const disconnectedElement = element || this.composite;
      const cleanup = () => {
        if (!disconnectedElement || !disconnectedElement.isConnected) {
          const domNodes = disconnectedElement ? disconnectedElement.children : undefined;
          this.bridge.disconnected(domNodes);
        }
      };
      const parentState = this.bridge.parent && this.bridge.parent.lifecycleState;
      const parentIsExiting = parentState === Constants.ContainerState.EXITED
        || parentState === Constants.ContainerState.DISPOSED;

      if (parentIsExiting) {
        // Terminal page exits need to clean up before the next App UI history state is written;
        // otherwise switcher-specific history can leak into the destination page.
        cleanup();
      } else {
        // Preact rendering can temporarily disconnect and reconnect the switcher;
        // those moves settle within a microtask.
        window.queueMicrotask(cleanup);
      }
    }

    /**
     * The module config contain the info about what should be rendered in the ojModule
     * This is controlled by the switcher.
     * @param  {Number} index The index in the array ojModule
     * @return {Object} The ojModule configuration at this index
     */
    getModuleConfigFromIndex(index) {
      return this.bridge.getModuleConfigFromIndex(index);
    }

    /**
     * Listener of the mutate event for the data property of type ADP
     *
     * @param {Object} event
     * @private
     */
    _mutateADPListener(event) {
      const { detail } = event;
      if (detail.add || detail.remove) {
        return this._queueSwitcherOperation(() => {
          if (detail.add) {
            let mutatePromise;
            if (!detail.remove) {
              this.bridge.arrayAdd(detail.add.data);
              mutatePromise = Promise.resolve();
            } else {
              const { removeArray, addArray } = resolveArrayMutation(detail);
              mutatePromise = Promise.resolve(this.bridge.arrayRemove(removeArray)).then(() => {
                this.bridge.arrayAdd(addArray);
              });
            }

            return mutatePromise.then(() => this._selectCurrentItem());
          }

          return Promise.resolve(this.bridge.arrayRemove(detail.remove.data))
            .then(() => this._selectCurrentItem());
        });
      }

      return undefined;
    }

    /**
     * Response to the ADP being refreshed. This is called from the refresh event
     * of the data property or when the component is initialized
     *
     * @param {Object} adp - The ArrayDataProvider
     * @return {Promise} a promise that resolves after the switcher data has been refreshed
     * @private
     */
    _refreshADP(adp) {
      // and show the initially selected switcher element
      return this._queueSwitcherOperation(() => Promise.resolve(this.bridge.refresh(adp))
        .then(() => this._selectCurrentItem()));
    }

    /**
     * Composite ViewModel callback called when a property on the composite is updated and before the
     * the changed event is fired.
     * @param {PropertyChangedContext} context
     */
    propertyChanged(context) {
      if (context.property === 'currentItem') {
        return this._queueSwitcherOperation(() => {
          Router.setBusyState();
          return this.bridge.currentItemChanged(context)
            .then(() => this._displayCurrentElement(this.bridge.getCurrentSwitcherElement()))
            .catch((error) => {
              logger.error(error);
              // In case of an error during the switch, restore the previous currentItem
              this.properties.currentItem = context.previousValue;
            })
            .finally(() => Router.clearBusyState());
        });
      }

      if (context.property === 'data') {
        const oldADP = context.previousValue;
        // This happen when a new Array is set on the ADP. Need to add listeners, create switcher
        // element and set the current item.
        if (oldADP && typeof oldADP.removeEventListener === 'function') {
          // Clean up listeners
          Object.keys(this.registeredListeners).forEach((eventType) => {
            oldADP.removeEventListener(eventType, this.registeredListeners[eventType]);
          });
          this.registeredListeners = {};
        }
        return this._initADP(context.value);
      }

      return Promise.resolve();
    }

    /**
     * The close function of the component
     *
     * @param  {String}   id The id of the switcherElement to close
     * @return {Promise}  a promise to a boolean
     */
    _close(id) {
      return this.bridge.close(id);
    }

    /**
     * The navigate function of the component
     *
     * @param   {Object}  options The navigate options, same as for a navigate action
     * @return  {Promise} a promise that resolve when navigation is complete
     */
    _navigate(options) {
      return this.bridge.navigate(options);
    }

    /**
     * Traverse all the ojModule present and only set display style on the current element.
     * This is needed when multiple switcher elements are added at once.
     *
     * @param {Object} currentSwitcherElt the current element
     * @private
     */
    _showCurrentElementAndHideOthers(currentSwitcherElt) {
      const indexToShow = currentSwitcherElt && currentSwitcherElt.index;
      // Retrieve all the top level oj-module nodes
      const moduleNodes = Array.from(this.composite.children).filter((e) => e.nodeName === 'OJ-MODULE');
      moduleNodes.forEach((moduleNode, index) => {
        if (index === indexToShow) {
          show(moduleNode);
        } else {
          hide(moduleNode);
        }
      });
    }

    /**
     * Shows the current item and notifies listeners once its page has completed its initial lifecycle.
     *
     * A switcher element's initialize() API starts its nested router and intentionally resolves before the
     * oj-module view model has run. Dispatching vbSwitcherContentChanged in that window lets an application
     * listener run against the new tab before its page variables have been initialized.
     *
     * @param {Object} currentSwitcherElt the current element to display
     * @return {Promise}
     * @private
     */
    _displayCurrentElement(currentSwitcherElt) {
      this._showCurrentElementAndHideOthers(currentSwitcherElt);

      const contentReadyPromise = currentSwitcherElt
        && typeof currentSwitcherElt.whenReady === 'function'
        ? currentSwitcherElt.whenReady()
        : Promise.resolve();

      return Promise.resolve(contentReadyPromise).then(() => {
        this._dispatchDisplayedContentChanged(currentSwitcherElt && currentSwitcherElt.id);
      });
    }

    /**
     * Dispatch the content changed event when the displayed switcher content changes.
     *
     * @param {String} item the id of the element being displayed
     * @private
     */
    _dispatchDisplayedContentChanged(item) {
      const nextItem = item || '';
      if (nextItem !== this._displayedItem) {
        const previousItem = this._displayedItem;
        this._displayedItem = nextItem;
        this.bridge.dispatchSwitcherContentChangedEvent(nextItem, previousItem);
      }
    }
  }

  return SwitcherViewModel;
});

